/**
 * Journal Cover Page - Personalized Welcome
 * Phase 4: "This journal belongs to..." customization
 */

import React, { useState, useEffect } from 'react'
import { useTheme } from '@/components/providers/ThemeProvider'
import { Edit2, Check, Sparkles } from 'lucide-react'
import { DoodleStars, BookmarkRibbon, WashiTape } from './DiaryDecorations'

interface JournalCoverProps {
  onClose?: () => void
  showOnStartup?: boolean
}

interface CoverSettings {
  ownerName: string
  subtitle: string
  quote: string
  decoration: 'minimal' | 'decorated' | 'elegant'
  showDate: boolean
}

const defaultQuotes = [
  "Every day is a new page...",
  "Progress, not perfection",
  "One step at a time",
  "Your story is worth telling",
  "Dream big, start small",
  "Today's efforts, tomorrow's results",
]

export const JournalCover: React.FC<JournalCoverProps> = ({
  onClose,
}) => {
  const { isDiary, diaryThemeConfig } = useTheme()
  
  const [isEditing, setIsEditing] = useState(false)
  const [settings, setSettings] = useState<CoverSettings>(() => {
    const saved = localStorage.getItem('horton-journal-cover')
    if (saved) {
      return JSON.parse(saved)
    }
    return {
      ownerName: 'My',
      subtitle: 'A Journey of Progress',
      quote: defaultQuotes[Math.floor(Math.random() * defaultQuotes.length)],
      decoration: 'decorated',
      showDate: true,
    }
  })

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('horton-journal-cover', JSON.stringify(settings))
  }, [settings])

  if (!isDiary) return null

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const colors = diaryThemeConfig?.light || {
    paperPrimary: 'var(--paper-cream)',
    inkPrimary: 'var(--ink-black)',
    inkSecondary: 'var(--ink-sepia)',
    bookmark: 'var(--bookmark-red)',
    accentPrimary: 'var(--horton-purple)',
  }

  const renderDecorations = () => {
    switch (settings.decoration) {
      case 'decorated':
        return (
          <>
            <BookmarkRibbon color="red" position="right" length={120} />
            <WashiTape color="pink" width={100} rotation={-5} position={{ top: '20px', left: '30px' }} />
            <WashiTape color="blue" width={80} rotation={3} position={{ bottom: '40px', right: '40px' }} />
            <DoodleStars count={3} spread="wide" className="absolute top-8 right-20" />
          </>
        )
      case 'elegant':
        return (
          <>
            <BookmarkRibbon color="gold" position="right" length={100} />
            <div 
              className="absolute top-4 left-4 right-4 h-1 opacity-30"
              style={{ background: `linear-gradient(90deg, transparent, ${colors.accentPrimary}, transparent)` }}
            />
            <div 
              className="absolute bottom-4 left-4 right-4 h-1 opacity-30"
              style={{ background: `linear-gradient(90deg, transparent, ${colors.accentPrimary}, transparent)` }}
            />
          </>
        )
      default:
        return null
    }
  }

  return (
    <div 
      className="relative w-full max-w-lg mx-auto p-8 rounded-lg"
      style={{
        background: colors.paperPrimary,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        minHeight: '400px',
      }}
    >
      {/* Decorations */}
      {renderDecorations()}

      {/* Edit button */}
      <button
        onClick={() => setIsEditing(!isEditing)}
        className="absolute top-4 right-4 p-2 rounded-full transition-colors z-20"
        style={{
          background: isEditing ? colors.accentPrimary : 'transparent',
          color: isEditing ? colors.paperPrimary : colors.inkSecondary,
        }}
      >
        {isEditing ? <Check size={18} /> : <Edit2 size={18} />}
      </button>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[350px] text-center">
        {/* the assistant Logo */}
        <div 
          className="text-5xl mb-4"
          style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.1))' }}
        >
          🐘
        </div>

        {/* Title */}
        <h1 
          className="text-lg tracking-widest uppercase mb-2"
          style={{ 
            fontFamily: "var(--font-ui, 'Nunito', sans-serif)",
            color: colors.inkSecondary,
            letterSpacing: '0.2em',
          }}
        >
          This Journal Belongs To
        </h1>

        {/* Owner Name */}
        {isEditing ? (
          <input
            type="text"
            value={settings.ownerName}
            onChange={(e) => setSettings(prev => ({ ...prev, ownerName: e.target.value }))}
            className="text-4xl text-center bg-transparent border-b-2 border-dashed outline-none w-full max-w-xs"
            style={{ 
              fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
              color: colors.inkPrimary,
              borderColor: colors.inkSecondary,
            }}
            autoFocus
          />
        ) : (
          <h2 
            className="text-4xl mb-2"
            style={{ 
              fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
              color: colors.inkPrimary,
            }}
          >
            {settings.ownerName}'s Journal
          </h2>
        )}

        {/* Subtitle */}
        {isEditing ? (
          <input
            type="text"
            value={settings.subtitle}
            onChange={(e) => setSettings(prev => ({ ...prev, subtitle: e.target.value }))}
            className="text-lg text-center bg-transparent border-b border-dashed outline-none w-full max-w-xs mt-2"
            style={{ 
              fontFamily: "var(--font-body, 'Lora', serif)",
              fontStyle: 'italic',
              color: colors.inkSecondary,
              borderColor: colors.inkSecondary,
            }}
          />
        ) : (
          <p 
            className="text-lg italic"
            style={{ 
              fontFamily: "var(--font-body, 'Lora', serif)",
              color: colors.inkSecondary,
            }}
          >
            {settings.subtitle}
          </p>
        )}

        {/* Decorative divider */}
        <div 
          className="flex items-center gap-2 my-6"
          style={{ color: colors.inkSecondary }}
        >
          <span>✦</span>
          <div className="w-20 h-px" style={{ background: colors.inkSecondary, opacity: 0.3 }} />
          <Sparkles size={16} />
          <div className="w-20 h-px" style={{ background: colors.inkSecondary, opacity: 0.3 }} />
          <span>✦</span>
        </div>

        {/* Quote */}
        {isEditing ? (
          <div className="w-full max-w-xs">
            <input
              type="text"
              value={settings.quote}
              onChange={(e) => setSettings(prev => ({ ...prev, quote: e.target.value }))}
              className="text-sm text-center bg-transparent border-b border-dashed outline-none w-full"
              style={{ 
                fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
                color: colors.inkSecondary,
                borderColor: colors.inkSecondary,
              }}
            />
            <div className="flex flex-wrap gap-2 mt-2 justify-center">
              {defaultQuotes.map((q, i) => (
                <button
                  key={i}
                  onClick={() => setSettings(prev => ({ ...prev, quote: q }))}
                  className="text-xs px-2 py-1 rounded-full border opacity-60 hover:opacity-100 transition-opacity"
                  style={{ 
                    borderColor: colors.inkSecondary,
                    color: colors.inkSecondary,
                  }}
                >
                  {q.substring(0, 15)}...
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p 
            className="text-sm italic max-w-xs"
            style={{ 
              fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
              color: colors.inkSecondary,
              opacity: 0.8,
            }}
          >
            "{settings.quote}"
          </p>
        )}

        {/* Date */}
        {settings.showDate && (
          <p 
            className="mt-6 text-xs tracking-wide"
            style={{ 
              fontFamily: "'Courier New', monospace",
              color: colors.inkSecondary,
              opacity: 0.6,
            }}
          >
            {currentDate}
          </p>
        )}

        {/* Decoration style selector (when editing) */}
        {isEditing && (
          <div className="mt-6 flex gap-2">
            {(['minimal', 'decorated', 'elegant'] as const).map((style) => (
              <button
                key={style}
                onClick={() => setSettings(prev => ({ ...prev, decoration: style }))}
                className="px-3 py-1 rounded-full text-xs capitalize transition-all"
                style={{
                  background: settings.decoration === style ? colors.accentPrimary : 'transparent',
                  color: settings.decoration === style ? colors.paperPrimary : colors.inkSecondary,
                  border: `1px solid ${colors.inkSecondary}`,
                }}
              >
                {style}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 rounded-full text-sm transition-all hover:scale-105"
          style={{
            background: colors.accentPrimary,
            color: colors.paperPrimary,
            fontFamily: "var(--font-ui, 'Nunito', sans-serif)",
          }}
        >
          Open Your Journal →
        </button>
      )}
    </div>
  )
}

export default JournalCover
